window.Plaintext = window.Plaintext = (window.Plaintext || {});
window.Plaintext.FocusManagement = Plaintext.FocusManagement || {};

/*
 * This library handles tools for creating and managing focus.
 *
 * == Simple Use Case
 * If you have a simple container containing all focusable elements in the proper order, you can simply
 * call enableFocusTrap to cause tabbing to loop through them, skipping all other elements on the page.
 * You'll probably also want to add an onEsc handler to close the focus trap with the Esc key.  
 * When the trap closes, call disableFocusTrap and offEsc.
 *
 * == Advanced Usage
 * A "focus link" defines Tab connection between a "from" and "to" element.  This allows fine-grained control over
 * tab order in a focus trap, when the visual tab order may not match the default.
 *  
 * Focus Links don't become active until enableFocusLinks is called.
 * 
 * Exports:
 * onEsc/offEsc: Convenience functions to register and unregister escape-key handlers which are commonly used to escape the focus trap.
 *
 * createFocusLoop([ elements ]): Creates a tab loop between all of the elements.  If elements are containers, all of the contained
 *   tabbables are included in the loop.  The last element loops back to the first.
 * 
 * createFocusLink(from, to): Creates a tab sequence between the specified elements.  Tabbing from "from" takes the user to "to" and shift-tabbing 
 *    performs the opposite.  Can be passed as the third parameter to enableFocusTrap.
 * 
 * createTrapLinkFromContainer(el): Loops the final tabbable element in "el" to the first tabbable element, creating a trap.  You probably prefer
 *    to use enableFocusTrap than using this specifically.
 * 
 * enable/disableFocusLinks: Until enable is called, focus links don't do anything.  This is fine-grained control you probably won't
 *    use directly unless you have a complicated widget that combines multiple elements out of tab order
 *
 * enable/disableFocusTrap: Creates and enables a focus trap preventing focus-escape from a specific element
 *
 * refreshFocusTrap: Can be called when a focus trap's elements have been rearranged and need to be updated.
 * 
 */
(function() {
  const FocusManagement = window.Plaintext.FocusManagement;
  var listenerHasAdjustedFocusThisTick = false;
  var escListeners = [];
  var focusStack = [];
  
  /***** UTILS *****/
  
  /** Known issue: Cannot be used on position:fixed elements.
    *   The offsetParent check returns null if the element has any parent that is display: none.  However, Chrome also returns null 
    *   for elements with display: fixed.  This usually isn't a problem since our trapped elements so far have been non-fixed elements.
    */
  function isAriaVisible(el) {
    if (!el) { return false; }
    if (el.getAttribute('aria-hidden')) { return false; }
    if (el.offsetParent === null) { return false; }
    
    return true;
  }
  
  /**
   * @param containers a single element or array of elements
   * @param includeTabRemovedElements If true, include tabindex=-1 elements
   * @return array[Node] Elements that can be tabbed to within the containers (including the container)
   */
  function findTabbables(containers, includeTabRemovedElements = false) {
    containers = Array.isArray(containers) ? containers : [ containers ];
    
    const tabbableSelector = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], area[href], button, iframe, object, embed, [tabindex]:not([disabled]), [contenteditable]';
    let allContainedNodes = [];

    containers.forEach(c => {
      const containedNodes = c.querySelectorAll(tabbableSelector);
      
      if (c.matches(tabbableSelector)) {
        allContainedNodes.push(c);
      }
      
      allContainedNodes = allContainedNodes.concat(Array.from(containedNodes));
    });
    
    if (!includeTabRemovedElements) {
      allContainedNodes = allContainedNodes.filter(n => (n.getAttribute('tabindex') !== '-1'));
    }
    
    // Ensure unique values by passing the collection through a Set
    let nodes = Array.from(new Set(allContainedNodes));
    
    // Ensure nodes are visible in aria.  This _may_ not be desired behavious in the future or in some applications, but
    // it's good for now.
    nodes = nodes.filter(isAriaVisible);
    
    // Sort according to DOM ordering
    nodes.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING) ? 1 : -1); 

    return nodes;
  }
  
  
  /***** Tools *****/
  
  function onEsc(fn) {
    var handler = function(e) {
      if (e.key !== 'Esc' && e.key !== 'Escape') { return; }
      fn();
    };
    
    escListeners.push({ fn: fn, handler: handler });
    window.addEventListener("keyup", handler);
  }
  
  function offEsc(fn) {
    // side-effect alert!  This "filter" also unregisters the event listener.
    escListeners = escListeners.filter(function(listener) {
      if (listener.fn === fn) {
        window.removeEventListener("keyup", listener.handler);
        return false;
      }
      else {
        return true;
      }
    });
  }
  
  /**
   * @return a function for the "keydown" event that listens for tabs and automatically wraps tab from the fromEl to the toEl, and
   * vice versa for shift+tab.
   * 
   * Note that we'd normally use keyup handlers to listen for keys, but Tab specifically takes effect on a keydown
   * 
   * Parameters can be an element or a prioritized array.  For arrays, only the first visible element will be treated.
   * 
   * @param fromEl Element being tabbed from
   * @param toEl Target after tabbing from fromEl
   * @param options Object { includeTabRemovedElements: Set to true to include tabindex=-1 elements in the tab order }
   */
  function createFocusLink(fromEl, toEl, options = {}) {
    return function(e) {
      // Prevent multiple tab overrides on a single event.
      if (listenerHasAdjustedFocusThisTick) { return; }

      if (e.key !== 'Tab') { return; }
      
      var nextElement;
      var elementFromElOrArray = function(elOrArray, getLast) {
        const tabbables = findTabbables(elOrArray, !!options.includeTabRemovedElements);
        const len = tabbables.length;
        if (len === 0) { return null; }
        return getLast ? tabbables[len - 1] : tabbables[0];
      };
      
      var visibleToEl = elementFromElOrArray(toEl);
      var visibleFromEl = elementFromElOrArray(fromEl, true);

      if (e.shiftKey) {
        if (document.activeElement === visibleToEl) {
          nextElement = visibleFromEl;
        }
      }
      else {
        if (document.activeElement === visibleFromEl) {
          nextElement = visibleToEl;
        }
      }

      if (nextElement) {
        nextElement.focus();
        e.preventDefault();
        listenerHasAdjustedFocusThisTick = true;
        window.setTimeout(function() { listenerHasAdjustedFocusThisTick = false; }, 0);
      }
    }
  }
  
  /**
   * @param links array of 2-element arrays.  The first index of each sub-array is fromEl, the second is toEl
   */
  function createFocusLinks(links, options = {}) {
    return links.map(link => createFocusLink(link[0], link[1]));
  }

  /**
   * @param loop array of elements representing the new tab order.  If an element is a container, all 
   *    tabbables within that container will be included, and the final one will link to the first
   *    tabbable of the next element.  The final element loops back to the first.
   *
   * @return links suitable for call to enableFocusLinks
   */  
  function createFocusLoop(loop, options = {}) {
    if (!loop || !loop.length) { return []; }
  
    const links = [];
    let lastEl = null;
    
    loop.forEach(el => {
      if (!el) { 
        console.error("Null element in loop listing", loop);
        return;
      }
      
      if (lastEl) { links.push([ lastEl, el ]); }
      lastEl = el;
    });
    
    links.push([ lastEl, loop[0] ]);
    return createFocusLinks(links, options);
  }
  
  /**
   * Create a focus link that loops the last focusable element in container to the first one.  Or an empty array.
   * 
   * @param containers element or array of elements
   */
  function createTrapLinkFromContainer(containers) {
    var elements = findTabbables(containers);
    
    if (!elements.length) { return null; }

    const trapLink = FocusManagement.createFocusLink(elements[elements.length - 1], elements[0]);
    return trapLink;
  }
  
  /**
   * Sets up event listeners for the specified focus link(s)
   *
   * @param links function|function[]|[{ from, to }] One or more functions created by createFocusLinkKeyDownListener
   */
  function enableFocusLinks(links) {
    links = Array.isArray(links) ? links : [links];
    links.forEach(function(link) { link && document.addEventListener('keydown', link); });
  }
  
  /**
   * Disables up event listeners for the specified focus link(s)
   *
   * @param links function|function[] One or more functions created by createFocusLinkKeyDownListener
   */
  function disableFocusLinks(links) {
    links= Array.isArray(links) ? links : [links];
    links.forEach(function(link) { link && document.removeEventListener('keydown', link); });
  }

  /**
   * @param containers element or array of elements
   * @param additionalFocusLinks function|function[]|[{from, to}] One or more functions created by createFocusLinkKeyDownListener, or a set of from/to pairs
   */  
  function enableFocusTrap(containers, newFocusedElement, additionalFocusLinks) {
    containers = Array.isArray(containers) ? containers : [ containers ];

    if (!containers || !containers.length || !containers[0]) {
      console.error('No containers specified');
      return;
    }
    
    if (containers[0].FocusManagement) {
      console.error("Focus trap enabled multiple times on...", containers);
      disableFocusTrap(containers);
    }

    const focusLinks = [];
    focusLinks.push(createTrapLinkFromContainer(containers));

    if (Array.isArray(additionalFocusLinks)) {
      additionalFocusLinks.forEach(link => {
        if (typeof(link) === 'function') {
          focusLinks.push(link);
        }
        else if (link.from && link.to) {
          focusLinks.push(FocusManagement.createFocusLink(link.from, link.to));
        }
        else {
          console.error("Could not create focus link from: ", link);
        }
      });
    }

    if (!newFocusedElement && newFocusedElement !== false) {
      var tabbables = findTabbables(containers);
      if (tabbables.length) { newFocusedElement = tabbables[0]; }
    }
  
    containers[0].FocusManagement = { 
      focusLinks: focusLinks,
      focusBeforeTrap: document.activeElement,
      containers: containers
    };

    enableFocusLinks(focusLinks);

    /* This is often called in response to a click, which will set focus on its own.  
     * Set focus *after* the click resolves so the caller doesn't have a responsibility 
     * to cancel the default click event behaviour */
    newFocusedElement && window.setTimeout(function() { 
	// Prevent scrolling since FocusManagement is often called in
	// conjunction with layout changes and modals that may unexpectedly
	// move the <body>
	newFocusedElement.focus({ preventScroll: true });
    }, 1);
  }
  
  function disableFocusTrap(containers, focusAfterClose) {
    containers = Array.isArray(containers) ? containers : [ containers ];
    const options = containers[0].FocusManagement || {};
    
    if (focusAfterClose !== false) {
      focusAfterClose = focusAfterClose || options.focusBeforeTrap;
    }

    FocusManagement.disableFocusLinks(options.focusLinks);
    delete containers[0].FocusManagement;
    focusAfterClose && focusAfterClose.focus();
  }
  
  function refreshFocusTrap(containers, newFocusedElement, additionalFocusLinks) {
    containers = Array.isArray(containers) ? containers : [ containers ];
    let focusBeforeTrap = null;
    
    if (containers[0].FocusManagement) {
      focusBeforeTrap = containers[0].FocusManagement.focusBeforeTrap;
      disableFocusTrap(containers);
    }
    
    enableFocusTrap(containers, newFocusedElement, additionalFocusLinks);
    
    if (focusBeforeTrap) {
      containers[0].FocusManagement.focusBeforeTrap = focusBeforeTrap;
    }
  }
  
  function pushFocus() {
    focusStack.push(document.activeElement);
  }
  
  function popFocus() {
    const el = focusStack.pop();
    el && el.focus();
    return el;
  }
  
  function toggleFocusables(node, enable) {
    enable ? enableFocusables(node) : disableFocusables(node);
  }
  
  function disableFocusables(node) {
    const focusables = findTabbables(node);

    focusables.forEach(f => {
      if (!f.hasAttribute('preserved-tab-index')) {
        const tabIndex = f.hasAttribute('tabindex') ? f.getAttribute('tabindex') : 'false';
        f.setAttribute('preserved-tab-index', tabIndex);
      }

      f.setAttribute('tabindex', -1);
    });
  }
  
  function enableFocusables(node) {
    const focusables = findTabbables(node);

    const fnEnable = f => {
      if (f.hasAttribute('preserved-tab-index')) {
        const preservedIndex = f.getAttribute('preserved-tab-index');
        f.removeAttribute('preserved-tab-index');
        
        if (preservedIndex === 'false') {
          f.removeAttribute('tabindex', preservedIndex);
        }
        else {
          f.setAttribute('tabindex', preservedIndex);
        }
      }
    }
    
    focusables.forEach(fnEnable);
    node.querySelectorAll('[preserved-tab-index]').forEach(fnEnable);
  }
  
  FocusManagement.onEsc = onEsc;
  FocusManagement.offEsc = offEsc;
  
  FocusManagement.createFocusLink = createFocusLink;
  FocusManagement.createFocusLinks = createFocusLinks;
  FocusManagement.createTrapLinkFromContainer = createTrapLinkFromContainer;

  FocusManagement.createFocusLoop = createFocusLoop;
  
  FocusManagement.enableFocusLinks = enableFocusLinks
  FocusManagement.disableFocusLinks = disableFocusLinks;
  
  FocusManagement.enableFocusTrap = enableFocusTrap;
  FocusManagement.disableFocusTrap = disableFocusTrap;
  FocusManagement.refreshFocusTrap = refreshFocusTrap;

  FocusManagement.pushFocus = pushFocus;
  FocusManagement.popFocus = popFocus;
  
  FocusManagement.enableFocusables = enableFocusables;
  FocusManagement.disableFocusables = disableFocusables;
  FocusManagement.toggleFocusables = toggleFocusables;
})();

export default window.Plaintext.FocusManagement;


/**
 * Copyright 2024 Plaintext Inc.
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */
